import { useCallback, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import type { RemovalOutcome } from 'api/referentialsApi';
import { useToast } from 'components/ui/toast';
import { apiErrorMessage } from 'utils/errorMessage';
import { isRemovedElsewhere, removalToast } from './confirmCopy';

interface State<T> {
  target: T | null;
  /** Shown in the confirmation only, which stays open on failure (AC11). */
  error: string | null;
  /** Set at click, before the request: a double-click must not send a second one. */
  pending: boolean;
}

type Action<T> = { type: 'ask'; target: T | null } | { type: 'start' } | { type: 'failed'; error: string };

const reducer = <T>(state: State<T>, action: Action<T>): State<T> => {
  switch (action.type) {
    case 'ask':
      return { target: action.target, error: null, pending: false };
    case 'start':
      return { ...state, error: null, pending: true };
    case 'failed':
      return { ...state, error: action.error, pending: false };
    default:
      return state;
  }
};

/**
 * The confirmation behind every « remove » of the referentials (D78). The toast follows the base's
 * outcome, not the announcement (a race); a value already gone elsewhere closes it (404, AC16).
 */
export const useRemovalConfirmation = <T extends { name: string }>(
  remove: (target: T) => Promise<{ outcome: RemovalOutcome }>
) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [state, dispatch] = useReducer(reducer<T>, { target: null, error: null, pending: false });

  const ask = useCallback((target: T | null) => dispatch({ type: 'ask', target }), []);

  const confirm = async () => {
    const { target } = state;
    if (!target || state.pending) return;
    dispatch({ type: 'start' });
    try {
      const { outcome } = await remove(target);
      toast.success(removalToast(t, outcome, target.name));
      ask(null);
    } catch (error) {
      const message = apiErrorMessage(t, error);
      if (!isRemovedElsewhere(error)) {
        dispatch({ type: 'failed', error: message });
        return;
      }
      toast.error(message);
      ask(null);
    }
  };

  return { ...state, ask, confirm };
};
